import React, { useMemo } from "react"
import {
    Heading,
    Flex,
    Avatar,
    AspectRatio,
    Button,
    Divider,
    Text,
    Box,
    Image,
    Icon,
} from "@chakra-ui/react"
import words from "lodash/words"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import { keyframes } from "@emotion/react"
import { format } from "date-fns"
import { Link } from "gatsby"

import SocialIcons from "./social-icons"
import { useRichTextContent } from "../shared/helpers"
import { FiArrowRight } from "react-icons/fi"

interface MainContentProps {
    blog: Queries.ContenfulBlogPostQuery["contentfulBlogPost"]
}

const MainContent: React.FC<MainContentProps> = ({ blog }) => {
    const timeToRead = useMemo(() => {
        const rawContent = blog?.content?.raw
        if (!rawContent) return 0
        const data = JSON.parse(rawContent)
        const dataString = documentToPlainTextString(data)
        let TTR
        // Change the WPM amount (240) below to higher number if your readers are faster
        // or lower if your readers are slower
        const avgWPM = 240
        const wordCount = words(dataString).length
        TTR = Math.round(wordCount / avgWPM)
        if (TTR === 0) {
            TTR = 1
        }
        return TTR
    }, [blog?.content?.raw])

    // eslint-disable-next-line
    // @ts-ignore
    const richTextContent = useRichTextContent(blog?.content)

    const slideRightGradient = keyframes`
        0% {
            background-position: 0% 50%;
        }
        50% {
            background-position: 100% 50%;
        }
        100% {
            background-position: 0% 50%;
        }
    `
    const slideAnimation = `${slideRightGradient} 6s ease infinite`

    return (
        <Box>
            <Heading pt={8} pb={6} id="blog-top">
                {blog?.title}
            </Heading>
            <Flex h="min-content">
                <Avatar
                    src={blog?.author?.avatar?.url || undefined}
                    name={blog?.author?.name || "Colin Hebert"}
                />
                <Flex ml={4} flexDir="column" justify="center">
                    <Text fontWeight="bold" lineHeight={1}>
                        {blog?.author?.name || "Colin Hebert"}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                        {format(
                            blog?.publicationDate
                                ? new Date(blog?.publicationDate)
                                : new Date(),
                            "MMM d, yyyy"
                        )}{" "}
                        &middot; {timeToRead} min read
                    </Text>
                </Flex>
            </Flex>
            <Box mt={4}>
                <Text fontWeight={700}>Share</Text>
                <SocialIcons direction="row" iconBoxSize="md" />
            </Box>
            <AspectRatio ratio={16 / 9} mt={6}>
                <Image
                    src={blog?.featuredImage?.url || undefined}
                    alt={blog?.title || "Blog post featured image"}
                    objectFit="cover"
                    objectPosition="center"
                    borderRadius={16}
                />
            </AspectRatio>
            {blog?.content && blog.content.raw && (
                <Box my={12}>{richTextContent}</Box>
            )}
            <Box>
                <Heading as="h2" fontSize="2xl" mb={4}>
                    Are you a veterinary professional?
                </Heading>
                <Text>
                    If you're in the veterinary field and you're interested in
                    giving your practice the extra hands it desperately needs,
                    book an 8 minute demo with Chckvet. Our platform helps busy
                    practices handle call overload, boost revenue per client,
                    and increase client satisfaction by providing actionable
                    insights and easy-to-use technology to help you stay on top
                    of everything.
                </Text>
                <Button
                    as={Link}
                    to="/book-a-demo"
                    bg="purple.600"
                    gridGap={1}
                    transition="0.2s"
                    bgGradient="linear(45deg, purple.600, pink.500)"
                    _hover={{
                        bg: "pink.700",
                        gridGap: 2,
                    }}
                    backgroundSize="175%"
                    mt={4}
                    size="lg"
                    animation={slideAnimation}
                    color="white"
                    boxShadow="lg"
                >
                    Book an 8 minute demo <Icon as={FiArrowRight} ml={2} />
                </Button>
            </Box>
            <Box mt={6}>
                <Text fontWeight={700}>
                    While you're at it, follow us on social media!
                </Text>
                <SocialIcons direction="row" iconBoxSize="md" />
            </Box>
            <Divider my={12} />
        </Box>
    )
}

export default MainContent
